import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_TAGS } from '../graphql/queries/tags';
import { DUST_THRESHOLD_USD } from '../constants/dust';

// Exit strategy filter: '' = all, 'unset' = no strategy, 'set' = has strategy, 'triggered' | 'approaching' = status
const EXIT_STRATEGY_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'unset', label: 'Unset' },
  { value: 'set', label: 'Set' },
  { value: 'triggered', label: 'Triggered' },
  { value: 'approaching', label: 'Approaching' }
];

const INVALIDATION_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'unset', label: 'Unset' },
  { value: 'set', label: 'Set' },
  { value: 'triggered', label: 'Triggered' },
  { value: 'approaching', label: 'Approaching' }
];

const PositionFilterModal = ({
  isOpen,
  assetKind = 'crypto',
  stockSectorOptions = [],
  stockTypeOptions = [],
  initialSearchTerm = '',
  initialHideDust = false,
  initialSelectedSector = null,
  initialSelectedInstrumentType = null,
  onClose,
  tokenGroups = [],
  tokenLocations = [],
  initialSelectedGroup = null,
  initialSelectedLocation = null,
  initialSelectedMarketCap = null,
  initialExitStrategyFilter = '',
  initialInvalidationFilter = '',
  initialSelectedTags = [],
  initialTagFilterMode = 'ANY',
  onApply,
  onReset
}) => {
  const isStock = assetKind === 'stock';
  const [tempSearchTerm, setTempSearchTerm] = useState(initialSearchTerm);
  const [tempHideDust, setTempHideDust] = useState(initialHideDust);
  const [tempSelectedSector, setTempSelectedSector] = useState(initialSelectedSector);
  const [tempSelectedInstrumentType, setTempSelectedInstrumentType] = useState(initialSelectedInstrumentType);
  const [tempSelectedGroup, setTempSelectedGroup] = useState(initialSelectedGroup);
  const [tempSelectedLocation, setTempSelectedLocation] = useState(initialSelectedLocation);
  const [tempSelectedMarketCap, setTempSelectedMarketCap] = useState(initialSelectedMarketCap);
  const [tempExitStrategyFilter, setTempExitStrategyFilter] = useState(initialExitStrategyFilter);
  const [tempInvalidationFilter, setTempInvalidationFilter] = useState(initialInvalidationFilter);
  const [tempSelectedTags, setTempSelectedTags] = useState(initialSelectedTags);
  const [tempTagFilterMode, setTempTagFilterMode] = useState(initialTagFilterMode);

  const { data: tagsData } = useQuery(GET_TAGS, { fetchPolicy: 'cache-and-network', skip: !isOpen });
  const allTags = tagsData?.tags || [];
  const untaggedValue = isStock ? 'none' : 'untagged';

  useEffect(() => {
    if (!isOpen) return;
    setTempSearchTerm(initialSearchTerm);
    setTempHideDust(initialHideDust);
    setTempSelectedSector(initialSelectedSector);
    setTempSelectedInstrumentType(initialSelectedInstrumentType);
    setTempSelectedGroup(initialSelectedGroup);
    setTempSelectedLocation(initialSelectedLocation);
    setTempSelectedMarketCap(initialSelectedMarketCap);
    setTempExitStrategyFilter(initialExitStrategyFilter);
    setTempInvalidationFilter(initialInvalidationFilter);
    setTempSelectedTags(initialSelectedTags);
    setTempTagFilterMode(initialTagFilterMode);
  }, [isOpen, initialSearchTerm, initialHideDust, initialSelectedSector, initialSelectedInstrumentType, initialSelectedGroup, initialSelectedLocation, initialSelectedMarketCap, initialExitStrategyFilter, initialInvalidationFilter, initialSelectedTags, initialTagFilterMode]);

  const handleApply = () => {
    if (typeof onApply === 'function') {
      onApply({
        searchTerm: tempSearchTerm,
        hideDust: tempHideDust,
        selectedSector: tempSelectedSector,
        selectedInstrumentType: tempSelectedInstrumentType,
        selectedGroup: tempSelectedGroup,
        selectedLocation: tempSelectedLocation,
        selectedMarketCap: tempSelectedMarketCap,
        exitStrategyFilter: tempExitStrategyFilter,
        invalidationFilter: tempInvalidationFilter,
        selectedTags: tempSelectedTags,
        tagFilterMode: tempTagFilterMode
      });
    }
    if (typeof onClose === 'function') onClose();
  };

  const handleReset = () => {
    setTempSearchTerm('');
    setTempHideDust(false);
    setTempSelectedSector(null);
    setTempSelectedInstrumentType(null);
    setTempSelectedGroup(null);
    setTempSelectedLocation(null);
    setTempSelectedMarketCap(null);
    setTempExitStrategyFilter('');
    setTempInvalidationFilter('');
    setTempSelectedTags([]);
    setTempTagFilterMode('ANY');
    if (typeof onReset === 'function') onReset();
    if (typeof onClose === 'function') onClose();
  };

  const toggleTag = (tagId) => {
    setTempSelectedTags(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (typeof onClose === 'function') onClose();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (typeof onApply === 'function') {
          onApply({
            searchTerm: tempSearchTerm,
            hideDust: tempHideDust,
            selectedSector: tempSelectedSector,
            selectedInstrumentType: tempSelectedInstrumentType,
            selectedGroup: tempSelectedGroup,
            selectedLocation: tempSelectedLocation,
            selectedMarketCap: tempSelectedMarketCap,
            exitStrategyFilter: tempExitStrategyFilter,
            invalidationFilter: tempInvalidationFilter,
            selectedTags: tempSelectedTags,
            tagFilterMode: tempTagFilterMode
          });
        }
        if (typeof onClose === 'function') onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, tempSearchTerm, tempHideDust, tempSelectedSector, tempSelectedInstrumentType, tempSelectedGroup, tempSelectedLocation, tempSelectedMarketCap, tempExitStrategyFilter, tempInvalidationFilter, tempSelectedTags, tempTagFilterMode, onApply, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div role="dialog" aria-modal="true" aria-labelledby="position-filters-title" className="relative bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md mx-4 p-4 max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 id="position-filters-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filters</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white">✕</button>
        </div>

        <div className="space-y-4">
          {isStock && (
            <>
              <div>
                <label htmlFor="stock-search-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                <input id="stock-search-filter" type="text" value={tempSearchTerm} onChange={(e) => setTempSearchTerm(e.target.value)} placeholder="Name or ticker" className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={tempHideDust} onChange={(e) => setTempHideDust(e.target.checked)} />
                Hide Dust (holdings below ${DUST_THRESHOLD_USD})
              </label>
              <div>
                <label htmlFor="stock-sector-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sector</label>
                <select id="stock-sector-filter" value={tempSelectedSector || ''} onChange={(e) => setTempSelectedSector(e.target.value || null)} className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm">
                  <option value="">All sectors</option>
                  {stockSectorOptions.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="stock-type-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instrument type</label>
                <select id="stock-type-filter" value={tempSelectedInstrumentType || ''} onChange={(e) => setTempSelectedInstrumentType(e.target.value || null)} className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm">
                  <option value="">All instrument types</option>
                  {stockTypeOptions.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
            </>
          )}
          {!isStock && <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Group</label>
            <select
              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm"
              value={tempSelectedGroup || ''}
              onChange={(e) => setTempSelectedGroup(e.target.value || null)}
            >
              <option value="">All Groups</option>
              {Array.isArray(tokenGroups) && tokenGroups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
              <option value="none">Ungrouped</option>
            </select>
          </div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
            <select
              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm"
              value={tempSelectedLocation || ''}
              onChange={(e) => setTempSelectedLocation(e.target.value || null)}
            >
              <option value="">All Locations</option>
              {Array.isArray(tokenLocations) && tokenLocations.map(location => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
              <option value="none">{isStock ? 'Unassigned' : 'No Location'}</option>
            </select>
          </div>

          {!isStock && <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Market Cap</label>
            <select
              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm"
              value={tempSelectedMarketCap || ''}
              onChange={(e) => setTempSelectedMarketCap(e.target.value || null)}
            >
              <option value="">All Market Caps</option>
              <option value="large">Large Caps</option>
              <option value="mid">Mid Caps</option>
              <option value="low">Low Caps</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Exit strategy</label>
            <select
              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm"
              value={tempExitStrategyFilter}
              onChange={(e) => setTempExitStrategyFilter(e.target.value)}
            >
              {EXIT_STRATEGY_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Invalidation</label>
            <select
              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm"
              value={tempInvalidationFilter}
              onChange={(e) => setTempInvalidationFilter(e.target.value)}
            >
              {INVALIDATION_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          </>}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tags</label>
              <select
                className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5"
                value={tempTagFilterMode}
                onChange={(e) => setTempTagFilterMode(e.target.value)}
              >
                <option value="ANY">Match Any</option>
                <option value="ALL">Match All</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto p-1">
              <button
                type="button"
                className={`text-xs px-2 py-0.5 rounded-full border ${
                  tempSelectedTags.includes(untaggedValue)
                    ? 'bg-gray-700 text-white border-gray-700 dark:bg-gray-200 dark:text-gray-800 dark:border-gray-200'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-500'
                }`}
                onClick={() => toggleTag(untaggedValue)}
              >
                Untagged
              </button>
              {allTags.map(tag => (
                <button
                  key={tag.id}
                  type="button"
                  className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-1 ${
                    tempSelectedTags.includes(tag.id)
                      ? 'text-white border-transparent'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-500'
                  }`}
                  style={tempSelectedTags.includes(tag.id) ? { backgroundColor: tag.color || '#6b7280' } : {}}
                  onClick={() => toggleTag(tag.id)}
                >
                  {!tempSelectedTags.includes(tag.id) && (
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color || '#6b7280' }} />
                  )}
                  {tag.name}
                </button>
              ))}
              {allTags.length === 0 && (
                <span className="text-xs text-gray-400">No tags created yet</span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-between">
          <button
            onClick={handleReset}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm"
          >
            Reset
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionFilterModal;


